import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import moment from "moment";

import { Event as ThreediEvent } from "@nens/threedi/openapi-client";

type EventDetailsAccordionProps = {
  eventKeys: string[];
  events: ThreediEvent;
  prettifyTitle: (value: string) => string;
};

function ViusaliseEventDetailValue({ eventValue }: { eventValue: any }) {
  if (eventValue === undefined) return "undefined"; // Undefined
  if (eventValue === null) return "null"; // Null
  if (eventValue === false) return "false"; // False
  if (eventValue === true) return "true"; // True

  if (Array.isArray(eventValue)) {
    if (!eventValue.length) return "None"; // Empty array

    if (Array.isArray(eventValue[0]))
      return (
        <UnorderedList>
          {eventValue.map((nestedEventValue: any, index: number) => (
            <ListItem key={index}>
              <ViusaliseEventDetailValue eventValue={nestedEventValue} /> {/* Array inside array */}
            </ListItem>
          ))}
        </UnorderedList>
      );

    return eventValue.map((nestedEventValue: any, index) => {
      if (nestedEventValue instanceof Date)
        return (index ? " - " : "") + moment(nestedEventValue).format("L HH:mm"); // Array of dates

      if (typeof nestedEventValue === "object")
        return <VisualiseEventsDetail event={nestedEventValue} />; // Array of objects

      return (
        (index ? " - " : "") + nestedEventValue // Array of strings or numbers
      );
    });
  }

  if (eventValue instanceof Date) return moment(eventValue).format("L HH:mm"); // Date

  if (typeof eventValue === "object") {
    if (Object.keys(eventValue).length === 0) return "none provided"; // Empty object
    return <VisualiseEventsDetail event={eventValue} />; // Object
  }

  try {
    new URL(eventValue);
    return (
      <a href={eventValue} target="_new">
        <Text m={0} textDecoration="underline" textColor="black">
          {eventValue}
        </Text>
      </a>
    );
  } catch (e) {
    const error = e;
  }

  return eventValue; // String or number
}

function VisualiseEventsDetail({ event }: { event: any }) {
  const eventKeyArray = Object.keys(event);
  return (
    <UnorderedList>
      {eventKeyArray.map((key: string, index: number) => {
        const eventValue = event[key];
        return (
          <ListItem key={key + index} py={1}>
            <strong>{key}:</strong> {<ViusaliseEventDetailValue eventValue={eventValue} />}
          </ListItem>
        );
      })}
    </UnorderedList>
  );
}

function EventDetailsAccordion({ eventKeys, events, prettifyTitle }: EventDetailsAccordionProps) {
  return (
    <Accordion marginTop="10px" allowMultiple defaultIndex={[]}>
      {eventKeys.map((key: string) => {
        const value = events[key as keyof ThreediEvent];
        return (
          <AccordionItem key={key}>
            <AccordionButton display="flex" justifyContent="space-between" paddingX={2}>
              <strong>{prettifyTitle(key)}</strong>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4} paddingX={2}>
              {Array.isArray(value) ? (
                value.map((arrayValue, index) => (
                  <VisualiseEventsDetail key={"event-detail" + index} event={arrayValue} />
                ))
              ) : (
                <VisualiseEventsDetail event={value} />
              )}
            </AccordionPanel>
          </AccordionItem>
        );
      })}
    </Accordion>
  );
}

export default EventDetailsAccordion;
